import React, { useEffect, useState } from 'react';
import { getBranches, getDoctors, getAllAppointments, getDepartments } from '../api';
import styles from './Dashboard.module.css';
import { formatDate, formatTimeRange } from '../utils/format';
import { useAuth } from '../AuthContext';

export default function DashboardPage() {
  const { user } = useAuth();
  const isDoctor = user?.role === 'DOCTOR';

  const [stats, setStats] = useState({
    branches: 0, departments: 0, doctors: 0,
    totalAppointments: 0, todayAppointments: 0,
    bookedAppointments: 0, cancelledAppointments: 0, completedAppointments: 0,
  });
  const [recentAppointments, setRecentAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      const today = new Date().toISOString().split('T')[0];

      if (isDoctor) {
        // Doctor — only fetch their own appointments
        const a = await getAllAppointments();
        let appointments = a.data.filter(ap => ap.doctor?.id === user.doctorId);
        setStats({
          branches: 0, departments: 0, doctors: 0,
          totalAppointments: appointments.length,
          todayAppointments: appointments.filter(a => a.appointmentDate === today).length,
          bookedAppointments: appointments.filter(a => a.status === 'BOOKED').length,
          cancelledAppointments: appointments.filter(a => a.status === 'CANCELLED').length,
          completedAppointments: appointments.filter(a => a.status === 'COMPLETED').length,
        });
        setRecentAppointments(
          [...appointments]
            .sort((a, b) => new Date(b.appointmentDate) - new Date(a.appointmentDate))
            .slice(0, 5)
        );
      } else {
        // Admin / Receptionist — fetch everything
        const [b, dep, d, a] = await Promise.all([
          getBranches(), getDepartments(), getDoctors(), getAllAppointments(),
        ]);
        const appointments = a.data;
        setStats({
          branches: b.data.length,
          departments: dep.data.length,
          doctors: d.data.length,
          totalAppointments: appointments.length,
          todayAppointments: appointments.filter(a => a.appointmentDate === today).length,
          bookedAppointments: appointments.filter(a => a.status === 'BOOKED').length,
          cancelledAppointments: appointments.filter(a => a.status === 'CANCELLED').length,
          completedAppointments: appointments.filter(a => a.status === 'COMPLETED').length,
        });
        setRecentAppointments(
          [...appointments]
            .sort((a, b) => new Date(b.appointmentDate) - new Date(a.appointmentDate))
            .slice(0, 5)
        );
      }
      setLoading(false);
    };
    fetchAll();
  }, []);

  // Doctor sees only appointment cards
  const allCards = [
    { label: 'Branches',           value: stats.branches,             icon: '🏢', color: '#1565c0', doctorHide: true },
    { label: 'Departments',        value: stats.departments,          icon: '🏬', color: '#6a1b9a', doctorHide: true },
    { label: 'Doctors',            value: stats.doctors,              icon: '👨‍⚕️', color: '#2e7d32', doctorHide: true },
    { label: "Today's Appointments", value: stats.todayAppointments,  icon: '📅', color: '#e65100', doctorHide: false },
    { label: 'Total Appointments', value: stats.totalAppointments,    icon: '📋', color: '#b71c1c', doctorHide: false },
    { label: 'Active Bookings',    value: stats.bookedAppointments,   icon: '✅', color: '#00695c', doctorHide: false },
    { label: 'Completed',          value: stats.completedAppointments,icon: '🏁', color: '#1565c0', doctorHide: false },
    { label: 'Cancelled',          value: stats.cancelledAppointments,icon: '❌', color: '#c62828', doctorHide: false },
  ];

  const cards = allCards.filter(c => !(isDoctor && c.doctorHide));

  const getStatusStyle = (status) => {
    switch (status) {
      case 'BOOKED':    return { background: '#e8f5e9', color: '#2e7d32' };
      case 'COMPLETED': return { background: '#e3f2fd', color: '#1565c0' };
      case 'CANCELLED': return { background: '#fce4e4', color: '#b71c1c' };
      default:          return { background: '#fff8e1', color: '#f57f17' };
    }
  };

  if (loading) return <p style={{ padding: 20 }}>Loading dashboard...</p>;

  return (
    <div>
      <h2 className={styles.heading}>
        {isDoctor ? `👨‍⚕️ Welcome, Dr. ${user.username}` : '📊 Dashboard'}
      </h2>

      <div className={styles.statsGrid}>
        {cards.map(c => (
          <div key={c.label} className={styles.statCard}>
            <div className={styles.icon} style={{ background: c.color + '18' }}>
              {c.icon}
            </div>
            <div>
              <div className={styles.value} style={{ color: c.color }}>{c.value}</div>
              <div className={styles.label}>{c.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.section}>
        <h3>🕐 {isDoctor ? 'My Recent Appointments' : 'Recent Appointments'}</h3>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Patient</th>
              {!isDoctor && <th>Doctor</th>}
              <th>Date</th>
              <th>Time</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {recentAppointments.length === 0 ? (
              <tr><td colSpan={isDoctor ? 4 : 5} style={{ textAlign: 'center', padding: 16 }}>No appointments yet</td></tr>
            ) : recentAppointments.map(a => (
              <tr key={a.id}>
                <td><strong>{a.patient?.name || '—'}</strong></td>
                {!isDoctor && <td>{a.doctor?.name || '—'}</td>}
                <td>{a.appointmentDate ? new Date(a.appointmentDate).toLocaleDateString('en-GB') : '—'}</td>
                <td>{a.slot?.startTime ? formatTimeRange(a.slot.startTime, a.slot.endTime) : '—'}</td>
                <td>
                  <span style={{
                    padding: '3px 10px', borderRadius: 12,
                    fontSize: 12, fontWeight: 600,
                    ...getStatusStyle(a.status),
                  }}>
                    {a.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
